import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { getStatusLabels, getPriorityLabels } from '../types';
import { TICKET_STATUSES } from '../utils/ticketStatus.shared';
import type { Ticket, TicketStatus } from '../types';
import { Plus, LayoutGrid, List, AlertTriangle, Inbox, SearchX, MoreHorizontal, Check, SlidersHorizontal } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import FilterBar, { type FilterChip, type FilterDef } from '../components/ui/FilterBar';
import SavedViews, { getRememberedView, type SystemView } from '../components/ui/SavedViews';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import Dialog from '../components/ui/Dialog';
import DataTable from '../components/DataTable';
import MenuButton from '../components/table/MenuButton';
import { ticketCard, ticketColumns, ticketRowActions } from '../components/table/ticketTable';
import { applyPatch, canonicalQuery, viewPatch, type ListSchema } from '../components/table/listParams';
import { useListParams, useDebouncedParam } from '../hooks/useListParams';
import { useIsDesktop } from '../components/ui/useOverlay';
import { useCloseTicket } from '../components/home/useCloseTicket';
import { TicketHasWorkDialog, ticketHasWorkMinutes } from '../components/tasks/TaskHasWorkDialog';
import { toast } from '../store/toastStore';
import { apiErrorMessage } from '../components/sales/shared';
import CreateTicketModal, { useCreateTicket } from '../components/CreateTicketModal';
import { canOpenTicket } from '../utils/ticketOrigin';
import { undoableDeleteTicket, DELETE_KINDS } from '../utils/undoableDeletes';
import { useWithoutHidden } from '../utils/undoableActions';
import { undoableTicketStatus, useWithPendingStatus } from '../utils/undoableTicketStatus';
import { ScreenLink } from '../components/permissions/ScreenLink';

const PAGE_SIZE = 20;

/**
 * Tickets list state lives in the URL (T-39, PRD §4.11): Back / Forward and a
 * shared link reproduce the view. The names are the ones Home, the dashboard
 * widgets and QuickCreate already link with (`scope`, `status`, `priority`,
 * `active`, `types=all`, `from=home`, `create=true`).
 */
const STATUS_RE = /^(planning|in_progress|in_review|testing|completed|new|waiting_customer|closed)(,(planning|in_progress|in_review|testing|completed|new|waiting_customer|closed))*$/;
const TICKET_PARAMS = {
  // D-144 — to_me / to_others: the account manager's "קריאות אליי" / "קריאות לאחרים".
  scope: { kind: 'enum', values: ['all', 'mine', 'mine_and_unassigned', 'to_me', 'to_others'] },
  q: { kind: 'string' },
  status: { kind: 'string', pattern: STATUS_RE },
  priority: { kind: 'enum', values: ['low', 'medium', 'high', 'urgent'] },
  active: { kind: 'bool' },
  /** D-154 (QX-83) — "לא נקראו" (server-side). */
  unread: { kind: 'bool' },
  /** Client-side: only the rows on this page (NL-01). */
  sla: { kind: 'enum', values: ['breached'] },
  /** FIX-B9 (D-161) — "קריאות שמחכות לאישור" (server-side, the cube's rule). */
  approval: { kind: 'enum', values: ['pending'] },
  /** FIX-B9 (D-161) — no agent on the ticket; with status=new: "קריאות חדשות שלא שויכו". */
  unassigned: { kind: 'bool' },
  types: { kind: 'enum', values: ['all'] },
  from: { kind: 'enum', values: ['home'] },
  page: { kind: 'int', default: 1, min: 1 },
} satisfies ListSchema;
/** What a view (system or saved) is made of. Search stays out: it is transient. */
const VIEW_KEYS = ['scope', 'status', 'priority', 'active', 'unread', 'sla', 'types', 'approval', 'unassigned'] as const;
type Scope = 'all' | 'mine' | 'mine_and_unassigned' | 'to_me' | 'to_others';
const LIST_ID = 'tickets';
/** The pre-v2 scope memory; read once as the fallback for the remembered view. */
const LEGACY_TAB_KEY = 'ote-tickets-tab';
const VIEW_MODE_KEY = 'ote-tickets-view';

const viewQuery = (v: Partial<Record<(typeof VIEW_KEYS)[number], string | boolean>>) =>
  canonicalQuery(TICKET_PARAMS, v as never, VIEW_KEYS);

function legacyScope(): Scope | null {
  try {
    const v = localStorage.getItem(LEGACY_TAB_KEY);
    return v === 'all' || v === 'mine' || v === 'mine_and_unassigned' ? v : null;
  } catch {
    return null;
  }
}

export default function TicketsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isDesktop = useIsDesktop();
  const list = useListParams(TICKET_PARAMS, { pageKey: 'page' });
  const f = list.values;
  const [showCreate, setShowCreate] = useState(false);
  // `?create=true&organization_id=<id>` (e.g. from the customer page) narrows
  // the customer picker in the create dialog to that customer's users.
  const [createOrgId, setCreateOrgId] = useState<string | undefined>(undefined);
  const isAdmin = user?.role === 'admin';
  const isAgent = user?.role === 'agent';
  const isInternal = isAdmin || isAgent;
  const roleScope: Scope = isAdmin ? 'all' : isInternal ? 'mine_and_unassigned' : 'mine';
  const defaultViewQuery = getRememberedView(LIST_ID, user?.id) ?? viewQuery({ scope: legacyScope() ?? roleScope });

  // One URL update on arrival: consume `create=true`, and give a URL without a
  // scope its view (a bare /tickets gets the remembered view; a deep link such
  // as `?active=true` keeps its filters and gets the remembered scope).
  useEffect(() => {
    const sp = list.searchParams;
    const wantsCreate = sp.get('create') === 'true';
    const needsScope = !f.scope;
    if (!wantsCreate && !needsScope) return;
    // P-45L (D-210): an employee opens no ticket - `create=true` is consumed and ignored.
    if (wantsCreate && canOpenTicket(user)) {
      setCreateOrgId(sp.get('organization_id') || undefined);
      setShowCreate(true);
    }
    list.update((prev) => {
      let next = prev;
      next.delete('create');
      next.delete('organization_id');
      next.delete('type');
      if (!next.get('scope')) {
        const bare = VIEW_KEYS.every((k) => !next.has(k));
        const remembered = new URLSearchParams(defaultViewQuery);
        if (bare) next = applyPatch(TICKET_PARAMS, next, viewPatch(TICKET_PARAMS, defaultViewQuery, VIEW_KEYS), { pageKey: 'page' });
        if (!next.get('scope')) next.set('scope', remembered.get('scope') || roleScope);
      }
      return next;
    }, { replace: true });
  }, [list, f.scope, defaultViewQuery, roleScope, user]);

  const scope = (f.scope || roleScope) as Scope;
  const page0 = Math.max(0, f.page - 1);
  const allTypes = f.types === 'all';
  const fromHome = f.from === 'home';
  const [searchText, setSearchText] = useDebouncedParam(f.q, (q) => list.set({ q }, { replace: true }));
  const [listView, setListView] = useState<'list' | 'cards'>(() => {
    try { return localStorage.getItem(VIEW_MODE_KEY) === 'cards' ? 'cards' : 'list'; } catch { return 'list'; }
  });
  const [showPrefs, setShowPrefs] = useState(false);
  const [deleting, setDeleting] = useState<Ticket | null>(null);

  const filterParam = scope === 'all' || f.q ? undefined : scope;
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['tickets', f.status, f.active, f.unread, f.priority, f.q, page0, scope, allTypes, f.approval, f.unassigned, f.sla],
    queryFn: () => api.getTickets({
      status: f.status || undefined,
      active: f.active || undefined,
      unread: f.unread || undefined,
      approval: f.approval === 'pending' ? 'pending' : undefined,
      unassigned: f.unassigned || undefined,
      sla: f.sla === 'breached' ? 'breached' : undefined,
      priority: f.priority || undefined,
      search: f.q || undefined,
      filter: filterParam,
      // D-146 (Q-95) — discussions have their own page ("דיונים").
      task_type: allTypes ? undefined : 'ticket',
      limit: PAGE_SIZE,
      offset: page0 * PAGE_SIZE,
    }),
    // Wait until the scope is in the URL, so the first visit fetches once.
    enabled: !!f.scope,
  });

  const createMutation = useCreateTicket(() => setShowCreate(false));

  // P-02c/F-04 — a service ticket with logged hours is not deleted (409
  // TICKET_HAS_WORK): the refusal is a dialog, not the banner.
  const [hasWork, setHasWork] = useState<{ ticket: Ticket; minutes: number } | null>(null);
  // P-27 (D-166 s.11): hidden at once, DELETE after 6 s, "בטל" cancels it (the toast
  // replaces "הקריאה נמחקה"). A refusal brings the row back with the same dialog.
  const [deleteFailed, setDeleteFailed] = useState(false);
  const requestDelete = (ticket: Ticket) => {
    setDeleteFailed(false);
    undoableDeleteTicket({
      qc: queryClient, id: ticket.id, taskType: ticket.task_type,
      onError: (err) => {
        const minutes = ticketHasWorkMinutes(err);
        if (minutes !== null) setHasWork({ ticket, minutes });
        else setDeleteFailed(true);
      },
    });
  };

  // Closing goes through the one close verb (B.23): the no-work-log and
  // open-sub-task checks, then a delayed commit with a 6 s "ביטול".
  const closer = useCloseTicket();

  // P-39 (D-199): "סמן כלא נקרא" from the row - personal; the list, the counts and the bell refresh.
  const markUnreadMutation = useMutation({
    mutationFn: (ticketId: string) => api.markTicketUnread(ticketId),
    onSuccess: () => {
      for (const key of ['tickets', 'conversations', 'conversations-grouped', 'workspace', 'home']) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
      toast('success', t('tickets.list.markedUnread'));
    },
    onError: () => toast('error', t('tickets.list.markUnreadFailed')),
  });

  const canCreate = canOpenTicket(user); // P-45L (D-210): the customer opens a ticket, an employee a task
  const canDelete = !!user && user.role === 'admin';
  const canChangeStatus = !!user && ['admin', 'agent'].includes(user.role);

  const total = data?.meta?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  // A shared or stale URL can point past the last page (rows were closed or deleted since).
  useEffect(() => {
    if (data?.success && totalPages > 0 && f.page > totalPages) list.set({ page: totalPages }, { replace: true });
  }, [data, totalPages, f.page, list]);

  const handleStatusSelect = (ticket: Ticket, s: TicketStatus) => {
    if (s === 'completed' && ticket.status !== 'completed' && ticket.status !== 'closed') {
      void closer.request({ id: ticket.id, title: ticket.title, task_type: ticket.task_type, parent_ticket_id: ticket.parent_ticket_id });
      return;
    }
    // P-29 (QX-107): saved only after the 6 s "ביטול" window (the customer notice goes then).
    undoableTicketStatus({ qc: queryClient, id: ticket.id, status: s, taskType: ticket.task_type ?? null });
  };

  // §18: cancelled tickets are hidden from the scoped lists and kept (marked)
  // in "all".
  const allRows = useMemo(() => data?.data ?? [], [data]);
  const rows = useWithPendingStatus(useWithoutHidden(DELETE_KINDS.ticket, allRows));
  const scoped = scope === 'all' ? rows : rows.filter((ticket) => !ticket.cancelled_at);
  const hiddenCancelled = rows.length - scoped.length;
  // FU-62 (P-28b): `sla=breached` is filtered by the server, so the count and the list are the same.
  const visible = scoped;

  /* ---------------- Views, filters, chips ---------------- */
  const systemViews: SystemView[] = [
    { id: 'all', name: t('tickets.list.viewAll'), query: viewQuery({ scope: 'all' }) },
    { id: 'mine', name: t('tickets.list.viewMine'), query: viewQuery({ scope: 'mine' }) },
    ...(isInternal ? [{ id: 'mine_and_unassigned', name: t('tickets.list.viewMineUnassigned'), query: viewQuery({ scope: 'mine_and_unassigned' }) }] : []),
    // D-144 — the account manager's two tabs (a ticket sent to another employee moves to "לאחרים").
    ...(isInternal ? [
      { id: 'to_me', name: t('tickets.list.viewToMe'), query: viewQuery({ scope: 'to_me' }) },
      { id: 'to_others', name: t('tickets.list.viewToOthers'), query: viewQuery({ scope: 'to_others' }) },
    ] : []),
    { id: 'sla', name: t('tickets.list.viewSla'), query: viewQuery({ scope: 'all', active: true, sla: 'breached' }) },
  ];
  const applyView = (query: string) => list.applyView(query, VIEW_KEYS, { drop: ['from'] });

  // DQ-085: the list shows only requests (task_type 'ticket') unless "all types" - so the
  // filter offers the request statuses of D-146 (חדשה / בטיפול / ממתינה ללקוח / הושלמה / נסגרה),
  // not the task statuses ("בתכנון", "בביצוע", "הושלם") the rows never carry.
  const statusOptions = allTypes
    ? Object.entries(getStatusLabels(t)).map(([value, label]) => ({ value, label }))
    : TICKET_STATUSES.map((value) => ({ value, label: t(`ticketStatus.${value}`) }));
  if (f.status.includes(',')) statusOptions.unshift({ value: f.status, label: t('home.filtered.openStatuses') });
  const filters: FilterDef[] = [
    {
      key: 'status',
      label: t('common.status'),
      value: f.status,
      options: statusOptions,
      allLabel: t('tickets.list.anyStatus'),
      hideChip: fromHome,
      onChange: (status) => list.set({ status, active: false }),
    },
    {
      key: 'priority',
      label: t('common.priority'),
      value: f.priority,
      options: Object.entries(getPriorityLabels(t)).map(([value, label]) => ({ value, label })),
      allLabel: t('tickets.list.anyPriority'),
      onChange: (priority) => list.set({ priority }),
    },
    {
      key: 'active',
      label: t('tickets.list.filterState'),
      value: f.active ? 'true' : '',
      options: [{ value: 'true', label: t('tickets.list.openOnly') }],
      allLabel: t('tickets.list.anyState'),
      onChange: (v) => list.set(v === 'true' ? { active: true, status: '' } : { active: false }),
    },
    {
      // D-154 (QX-83) — "לא נקראו".
      key: 'unread',
      label: t('tickets.list.filterRead'),
      value: f.unread ? 'true' : '',
      options: [{ value: 'true', label: t('tickets.list.unreadOnly') }],
      allLabel: t('tickets.list.anyRead'),
      hideChip: fromHome,
      onChange: (v) => list.set({ unread: v === 'true' }),
    },
    {
      key: 'sla',
      label: t('tickets.list.filterSla'),
      value: f.sla,
      options: [{ value: 'breached', label: t('tickets.list.slaBreached') }],
      allLabel: t('tickets.list.anySla'),
      clientSide: true,
      onChange: (sla) => list.set({ sla }),
    },
    ...(isInternal ? [
      {
        // FIX-B9 (D-161) — "מחכות לאישור".
        key: 'approval',
        label: t('tickets.list.filterApproval'),
        value: f.approval,
        options: [{ value: 'pending', label: t('tickets.list.approvalPending') }],
        allLabel: t('tickets.list.anyApproval'),
        hideChip: fromHome,
        onChange: (approval: string) => list.set({ approval }),
      },
      {
        // FIX-B9 (D-161) — "לא שויכו".
        key: 'unassigned',
        label: t('tickets.list.filterAssignment'),
        value: f.unassigned ? 'true' : '',
        options: [{ value: 'true', label: t('tickets.list.unassignedOnly') }],
        allLabel: t('tickets.list.anyAssignment'),
        hideChip: fromHome,
        onChange: (v: string) => list.set({ unassigned: v === 'true' }),
      },
    ] : []),
  ];

  const clearHomeFilter = () => list.applyView(defaultViewQuery, VIEW_KEYS, { drop: ['from'] });
  const clearAll = () => list.set({ status: '', priority: '', active: false, unread: false, sla: '', types: '', q: '', approval: '', unassigned: false }, { drop: ['from'] });
  const chips: FilterChip[] = [];
  if (fromHome) {
    chips.push({
      key: 'home',
      label: t('home.filtered.label', { label: t(`home.filtered.${f.unread ? 'unread' : f.approval ? 'pendingApproval' : f.sla ? 'slaBreached' : f.unassigned ? 'unassignedNew' : scope === 'mine_and_unassigned' ? 'unassigned' : 'open'}`) }),
      onRemove: clearHomeFilter,
    });
  } else if (allTypes) {
    chips.push({ key: 'types', label: t('tickets.list.chipAllTypes'), onRemove: () => list.set({ types: '' }) });
  }
  const filtersActive = !!(f.q || f.status || f.priority || f.active || f.unread || f.sla || f.approval || f.unassigned || allTypes || fromHome);

  /* ---------------- Summary (a count is a promise) ---------------- */
  let summary: ReactNode = null;
  if (data?.success) {
    const parts: string[] = [
      t('uxTickets.count', { count: total }),
    ];
    if (hiddenCancelled > 0) parts.push(t('tickets.list.cancelledHidden', { count: hiddenCancelled }));
    if (f.q && scope !== 'all') parts.push(t('tickets.list.searchAllScopes'));
    summary = parts.join(' · ');
  }

  const empty = filtersActive ? (
    <EmptyState
      icon={SearchX}
      title={t('uxTickets.noMatchTitle')}
      description={t('tickets.list.noMatchHint')}
      action={<button type="button" className="btn btn-secondary" onClick={clearAll}>{t('tickets.list.clearFilters')}</button>}
    />
  ) : (
    // C-05 (clarity, D-02): an employee opens no ticket (customers do, in the portal), so the empty
    // list says where the work is: "המשימות שלי". The default scope "שלי + פנויות לטיפול" says so when
    // it is empty, with one step to every ticket. Managers and customers keep today's text.
    <EmptyState
      icon={Inbox}
      title={isAgent && scope === 'mine_and_unassigned' ? t('uxTickets.scopeEmptyNote') : t('tickets.noTickets')}
      description={isAgent ? t('uxTickets.emptyHintAgent') : t('uxTickets.emptyHint')}
      action={(isAgent || scope !== 'all') ? (
        <span className="inline-flex flex-wrap items-center justify-center gap-2">
          {isAgent && <ScreenLink to="/tasks?scope=mine" closed="hide" className="btn btn-secondary">{t('uxTickets.myTasksLink')}</ScreenLink>}
          {scope !== 'all' && (
            <button type="button" className={isAgent ? 'btn btn-ghost' : 'btn btn-secondary'} onClick={() => applyView(viewQuery({ scope: 'all' }))}>{t('uxTickets.showAll')}</button>
          )}
        </span>
      ) : undefined}
    />
  );

  const columns = ticketColumns({ t, canChangeStatus, statusOf: (ticket) => ticket.status, onStatusSelect: handleStatusSelect });

  const selectView = (v: 'list' | 'cards') => {
    setListView(v);
    try { localStorage.setItem(VIEW_MODE_KEY, v); } catch { /* preference only */ }
  };
  const check = <Check size={16} strokeWidth={1.75} className="text-accent-text" aria-hidden="true" />;
  const pageMenu = isDesktop && (
    <MenuButton
      label={t('tickets.list.pageMenu')}
      showTooltip
      popoverClassName="w-56 p-1.5"
      triggerClassName="btn-icon text-text-2 hover:bg-surface-subtle hover:text-text aria-expanded:bg-surface-subtle"
      items={[
        { key: 'list', label: t('tickets.list.viewModeList'), icon: <List size={16} strokeWidth={1.75} />, trailing: listView === 'list' ? check : undefined, onSelect: () => selectView('list') },
        { key: 'cards', label: t('tickets.list.viewModeCards'), icon: <LayoutGrid size={16} strokeWidth={1.75} />, trailing: listView === 'cards' ? check : undefined, onSelect: () => selectView('cards') },
      ]}
    >
      <MoreHorizontal size={18} strokeWidth={1.75} aria-hidden="true" />
    </MenuButton>
  );

  return (
    <div>
      <PageHeader
        title={t('tickets.title')}
        actions={canCreate && (
          <button type="button" className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={16} aria-hidden="true" />
            <span>{t('tickets.newTicket')}</span>
          </button>
        )}
      />

      <FilterBar
        className="mb-4"
        leading={(
          <SavedViews
            listId={LIST_ID}
            systemViews={systemViews}
            currentQuery={list.query(VIEW_KEYS)}
            onApply={applyView}
            extraItems={scope === 'mine' ? [{
              key: 'prefs',
              label: t('tickets.list.minePrefs'),
              icon: <SlidersHorizontal size={16} strokeWidth={1.75} />,
              onSelect: () => setShowPrefs(true),
            }] : []}
          />
        )}
        search={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder={t('tickets.searchPlaceholder')}
        filters={filters}
        chips={chips}
        onClearAll={clearAll}
        summary={summary}
        end={pageMenu}
      />

      {showPrefs && (
        <TicketPreferencesModal
          onClose={() => setShowPrefs(false)}
          onSaved={() => { setShowPrefs(false); queryClient.invalidateQueries({ queryKey: ['tickets'] }); }}
        />
      )}

      {deleteFailed && (
        <div role="alert" className="mb-4 flex items-start gap-2 rounded-lg bg-danger-50 px-4 py-3 text-sm text-danger-700">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
          {t('tickets.deleteError')}
        </div>
      )}

      {showCreate && <CreateTicketModal
        organizationId={createOrgId}
        onClose={() => { setShowCreate(false); setCreateOrgId(undefined); }}
        onSubmit={(d) => createMutation.mutate(d)}
        loading={createMutation.isPending}
        error={createMutation.error}
      />}

      <DataTable
        columns={columns}
        data={visible}
        keyField="id"
        caption={t('tickets.list.caption')}
        density="compact"
        stickyHeader
        mobile="cards"
        layout={listView === 'cards' && isDesktop ? 'cards' : 'table'}
        getRowHref={(ticket) => `/tickets/${ticket.id}?from=/tickets`}
        rowLabel={(ticket) => ticket.title}
        card={(ticket) => ticketCard(ticket, ticket.status, t, { canChangeStatus, onStatusSelect: (s) => handleStatusSelect(ticket, s) })}
        rowClassName={(ticket) => (ticket.is_unread ? 'font-semibold' : '')}
        rowActions={(ticket) => ticketRowActions(ticket, ticket.status, {
          t,
          onMarkUnread: () => markUnreadMutation.mutate(ticket.id),
          canChangeStatus,
          canDelete,
          onStatusSelect: (s) => handleStatusSelect(ticket, s),
          onDelete: () => setDeleting(ticket),
        })}
        isLoading={isPending}
        skeletonRows={8}
        error={error}
        errorMessage={t('tickets.loadError')}
        onRetry={() => { void refetch(); }}
        emptyState={empty}
        pagination={data?.success ? {
          page: page0 + 1,
          pageCount: totalPages,
          total,
          onChange: (p) => list.set({ page: Math.min(Math.max(1, p), Math.max(1, totalPages)) }),
        } : undefined}
      />

      <ConfirmDialog
        open={!!deleting}
        title={t('tickets.list.deleteTitle', { title: deleting?.title ?? '' })}
        description={t('tickets.list.deleteBody')}
        confirmLabel={t('tickets.list.deleteConfirm')}
        danger
        onConfirm={() => { if (deleting) requestDelete(deleting); setDeleting(null); }}
        onCancel={() => setDeleting(null)}
      />

      {hasWork && (
        <TicketHasWorkDialog
          minutes={hasWork.minutes}
          onCloseTicket={canChangeStatus && !['completed', 'closed', 'cancelled'].includes(hasWork.ticket.status) && !hasWork.ticket.cancelled_at
            ? () => {
              const tk = hasWork.ticket;
              setHasWork(null);
              void closer.request({ id: tk.id, title: tk.title, task_type: tk.task_type, parent_ticket_id: tk.parent_ticket_id });
            }
            : undefined}
          onClose={() => setHasWork(null)}
        />
      )}

      {closer.dialogs}
    </div>
  );
}


function TicketPreferencesModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const prefs = user?.ticket_filter_preferences || { assigned: true, commented: true, created: false, include_closed: false };
  const [assigned, setAssigned] = useState(prefs.assigned);
  const [commented, setCommented] = useState(prefs.commented);
  const [created, setCreated] = useState(prefs.created);
  const [includeClosed, setIncludeClosed] = useState(prefs.include_closed);

  const mutation = useMutation({
    mutationFn: () => api.updateTicketPreferences({ assigned, commented, created, include_closed: includeClosed }),
    onSuccess: onSaved,
    onError: (err) => toast('error', apiErrorMessage(err, t('common.saveError'))),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('tickets.filterTitle')}
      size="sm"
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary">{t('common.cancel')}</button>
          <button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending} className="btn btn-primary">
            {mutation.isPending ? t('common.saving') : t('common.save')}
          </button>
        </>
      )}
    >
      <div className="space-y-3">
        <label className="flex min-h-touch items-center gap-2 cursor-pointer md:min-h-0">
          <input type="checkbox" checked={assigned} onChange={(e) => setAssigned(e.target.checked)} className="w-4 h-4 accent-primary-700" />
          <span className="text-sm text-text-2">{t('tickets.filterAssigned')}</span>
        </label>
        <label className="flex min-h-touch items-center gap-2 cursor-pointer md:min-h-0">
          <input type="checkbox" checked={commented} onChange={(e) => setCommented(e.target.checked)} className="w-4 h-4 accent-primary-700" />
          <span className="text-sm text-text-2">{t('tickets.filterCommented')}</span>
        </label>
        <label className="flex min-h-touch items-center gap-2 cursor-pointer md:min-h-0">
          <input type="checkbox" checked={created} onChange={(e) => setCreated(e.target.checked)} className="w-4 h-4 accent-primary-700" />
          <span className="text-sm text-text-2">{t('tickets.filterCreated')}</span>
        </label>
        <div className="h-px bg-border" role="separator" />
        <label className="flex min-h-touch items-center gap-2 cursor-pointer md:min-h-0">
          <input type="checkbox" checked={includeClosed} onChange={(e) => setIncludeClosed(e.target.checked)} className="w-4 h-4 accent-primary-700" />
          <span className="text-sm text-text-2">{t('tickets.filterIncludeClosed')}</span>
        </label>
      </div>
    </Dialog>
  );
}
